import { RefObject, useEffect, useRef } from 'react'

import { useVariableRef } from 'src/hooks/useVariableRef'

// There is no fallback painter, so a browser without the API goes unpainted rather than throwing.
const supportsCSSHighlightApi = () => typeof Highlight !== 'undefined' && typeof CSS !== 'undefined' && !!CSS.highlights

type UseCSSHighlightOptions = {
    name: string
    containerRef: RefObject<HTMLElement | null>
    // Distinct from `resolveRanges` returning [] ("not mounted yet") — false clears and stops observing.
    active: boolean
    /**
     * Must be memoised on the ranges it resolves. Re-resolution after an edit keeps the same
     * `highlightKey`, so a new identity here is the only signal that the paint is stale.
     */
    resolveRanges: () => Range[]
    /**
     * Decides which styles win where two highlights overlap, higher first, and only for CSS
     * properties both of them set. At equal priority the most recently registered one wins, which
     * changes whenever one highlight re-registers without the other, e.g. on each new playing word.
     */
    priority?: number
    // `onHighlightPainted` runs once per key change, not on every repaint, so scrolling is not re-triggered.
    highlightKey?: unknown
    onHighlightPainted?: (ranges: Range[]) => void
}

export const useCSSHighlight = ({
    name,
    containerRef,
    active,
    resolveRanges,
    priority = 0,
    highlightKey,
    onHighlightPainted,
}: UseCSSHighlightOptions) => {
    const lastPaintedKeyRef = useRef<unknown>(null)
    const onHighlightPaintedRef = useVariableRef(onHighlightPainted)

    useEffect(() => {
        if (!supportsCSSHighlightApi()) {
            return
        }

        const container = containerRef.current
        if (!container || !active) {
            CSS.highlights.delete(name)

            return
        }

        const paint = () => {
            const domRanges = resolveRanges()

            if (!domRanges.length) {
                CSS.highlights.delete(name)

                return
            }

            const highlight = new Highlight(...domRanges)
            highlight.priority = priority
            CSS.highlights.set(name, highlight)

            if (lastPaintedKeyRef.current !== highlightKey) {
                lastPaintedKeyRef.current = highlightKey
                onHighlightPaintedRef.current?.(domRanges)
            }
        }

        paint()

        // Re-paint when virtualization/edits detach ranged nodes; one frame coalesces burst updates.
        let frame = 0
        const observer = new MutationObserver(() => {
            if (frame) {
                return
            }
            frame = requestAnimationFrame(() => {
                frame = 0
                paint()
            })
        })
        observer.observe(container, { childList: true, subtree: true, characterData: true })

        return () => {
            observer.disconnect()
            if (frame) {
                cancelAnimationFrame(frame)
            }
            CSS.highlights.delete(name)
        }
    }, [name, containerRef, active, highlightKey, priority, resolveRanges, onHighlightPaintedRef])
}
